import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Activity } from "lucide-react";
import { toast } from "sonner";
import { runAssetSelfTest } from "@/lib/asset-self-test";

export const Route = createFileRoute("/login")({
  component: LoginPage,
  head: () => ({ meta: [{ title: "Sign in — InboxPulse" }] }),
});

function LoginPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/dashboard" });
  }, [user, loading, navigate]);

  // Asset-Selbsttest: nur einmal pro Tab ausführen. Zwei aufeinanderfolgende
  // Fehlläufe (mit 1,2 s Pause) gelten als reproduzierbar → Weiterleitung
  // auf /troubleshooting. Einzelne Aussetzer werden ignoriert.
  useEffect(() => {
    const SESSION_KEY = "login:self-test-done";
    if (typeof window === "undefined") return;
    try {
      if (window.sessionStorage.getItem(SESSION_KEY) === "1") return;
    } catch {
      return;
    }
    let cancelled = false;
    void (async () => {
      const first = await runAssetSelfTest();
      if (cancelled) return;
      // Klares Block-Indiz nur, wenn mind. 2 unabhängige Probes alle
      // gescheitert sind. Einzelne Probe → zu unsicher (Timeout, Race).
      if (!first.allFailed || first.totalCount < 2) {
        try {
          window.sessionStorage.setItem(SESSION_KEY, "1");
        } catch {
          /* ignore */
        }
        return;
      }
      // Reproduzierbarkeit absichern: zweiter Lauf nach kurzer Pause.
      await new Promise((r) => window.setTimeout(r, 1200));
      if (cancelled) return;
      const second = await runAssetSelfTest();
      if (cancelled) return;
      try {
        window.sessionStorage.setItem(SESSION_KEY, "1");
      } catch {
        /* ignore */
      }
      // Zweiter Lauf muss ebenfalls allFailed sein UND dieselben URLs
      // betreffen wie der erste — nur dann ist es reproduzierbar.
      const firstUrls = new Set(first.probes.map((p) => p.url));
      const sameTargets =
        second.probes.length > 0 && second.probes.every((p) => firstUrls.has(p.url));
      if (second.allFailed && second.totalCount >= 2 && sameTargets) {
        console.warn("[login] Asset-Selbsttest reproduzierbar fehlgeschlagen", {
          first,
          second,
        });
        navigate({ to: "/troubleshooting", search: { from: "login" } });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (err: any) {
      toast.error(err.message ?? "Authentication failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[radial-gradient(ellipse_at_top,_var(--primary)/0.15,_transparent_50%)]">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="size-9 rounded-lg bg-[image:var(--gradient-primary)] flex items-center justify-center shadow-[var(--shadow-glow)]">
            <Activity className="size-5 text-primary-foreground" />
          </div>
          <span className="text-xl font-semibold tracking-tight">InboxPulse</span>
        </Link>
        <Card className="p-8 bg-card/80 backdrop-blur border-border">
          <h1 className="text-2xl font-semibold mb-1">Welcome back</h1>
          <p className="text-sm text-muted-foreground mb-6">Sign in to your dashboard</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-[image:var(--gradient-primary)] hover:opacity-90 transition"
            >
              {busy ? "Please wait..." : "Sign in"}
            </Button>
          </form>
          <p className="mt-4 text-xs text-muted-foreground text-center">
            Noch keinen Account?{" "}
            <Link to="/signup" className="text-primary hover:underline">
              Jetzt registrieren
            </Link>
          </p>
        </Card>
      </div>
    </div>
  );
}
